import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/layout/Layout';
import Navbar from '../../components/layout/Navbar';
import DataTable from '../../components/common/DataTable';
import Badge from '../../components/common/Badge';
import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import { getAdmins, createAdmin, updateAdmin, deleteAdmin } from '../../api/superAdminApi';
import { ShieldCheck, Eye, Power, Trash2, Phone, Mail, Plus } from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

const ManageAdmins = () => {
  const [admins, setAdmins]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [modalType, setModalType] = useState(null); // 'view' | 'delete' | 'create'
  
  // Create form
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', password: '' });
  const [submitting, setSubmitting] = useState(false);

  const fetchAdmins = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getAdmins();
      setAdmins(res.data?.data?.admins || []);
    } catch {
      toast.error('Failed to load admins');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAdmins(); }, [fetchAdmins]);

  const handleToggle = async (admin) => {
    try {
      await updateAdmin(admin.user_id, { is_active: !admin.is_active });
      toast.success(`${admin.name} ${admin.is_active ? 'suspended' : 'activated'}`);
      fetchAdmins();
    } catch {
      toast.error('Failed to update admin status');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteAdmin(selected.user_id);
      toast.success(`${selected.name} deleted`);
      setModalType(null); setSelected(null);
      fetchAdmins();
    } catch {
      toast.error('Failed to delete admin');
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      await createAdmin(formData);
      toast.success('Admin created successfully!');
      setModalType(null);
      setFormData({ name: '', email: '', phone: '', password: '' });
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create admin');
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    { header: '#',       id: 'no',      cell: ({ row: { original: a } }) => (
      <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
        <span className="text-purple-700 text-xs font-bold">{a.name?.charAt(0).toUpperCase()}</span>
      </div>
    )},
    { header: 'Name',    accessorKey: 'name',  cell: ({ row: { original: a } }) => (
      <div><p className="font-semibold text-gray-900">{a.name}</p><p className="text-xs text-gray-400">{a.email}</p></div>
    )},
    { header: 'Phone',   accessorKey: 'phone' },
    { header: 'Joined Date',  accessorKey: 'createdAt', cell: ({ row: { original: a } }) => (
      <span className="text-xs text-gray-500">{a.createdAt ? format(new Date(a.createdAt), 'dd MMM yyyy') : '—'}</span>
    )},
    { header: 'Actions', id: 'actions', cell: ({ row: { original: a } }) => (
      <div className="flex items-center gap-2">
        <Button size="sm" variant="ghost" icon={Eye} onClick={() => { setSelected(a); setModalType('view'); }} />
        <Button size="sm" variant={a.is_active ? 'danger' : 'success'} icon={Power}
          onClick={() => handleToggle(a)}>
          {a.is_active ? 'Suspend' : 'Activate'}
        </Button>
      </div>
    )},
  ];

  return (
    <Layout>
      <Navbar title="Manage Admins" subtitle={`${admins.length} registered admins`} onRefresh={fetchAdmins} loading={loading} />
      <div className="p-6">
        <div className="card">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="bg-purple-100 p-2 rounded-xl"><ShieldCheck size={20} className="text-purple-600" /></div>
              <div>
                <h2 className="font-bold text-gray-900">Admin Registry</h2>
                <p className="text-xs text-gray-500">Manage all system administrators</p>
              </div>
            </div>
            <Button onClick={() => setModalType('create')} icon={Plus}>Add Admin</Button>
          </div>
          <DataTable columns={columns} data={admins} loading={loading} emptyMessage="No admins found" />
        </div>
      </div>

      {/* Create Modal */}
      <Modal open={modalType === 'create'} onClose={() => setModalType(null)} title="Add New Admin">
        <form onSubmit={handleCreate} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input required type="text" className="w-full px-4 py-2 border rounded-lg"
              value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input type="email" className="w-full px-4 py-2 border rounded-lg"
              value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
            <input required type="text" className="w-full px-4 py-2 border rounded-lg"
              value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
            <input required type="password" className="w-full px-4 py-2 border rounded-lg"
              value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})} />
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <Button type="button" variant="secondary" onClick={() => setModalType(null)}>Cancel</Button>
            <Button type="submit" loading={submitting}>Create Admin</Button>
          </div>
        </form>
      </Modal>

      {/* View Modal */}
      <Modal open={modalType === 'view'} onClose={() => setModalType(null)} title="Admin Details">
        {selected && (
          <div className="space-y-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-purple-100 rounded-2xl flex items-center justify-center">
                <span className="text-2xl font-bold text-purple-700">{selected.name?.charAt(0)}</span>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">{selected.name}</h3>
                <Badge status={selected.is_active ? 'active' : 'inactive'} />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Email', value: selected.email, icon: Mail },
                { label: 'Phone', value: selected.phone, icon: Phone },
                { label: 'Joined', value: selected.createdAt ? format(new Date(selected.createdAt), 'dd MMM yyyy') : '—', icon: null },
              ].map(item => (
                <div key={item.label} className="bg-gray-50 rounded-xl p-3">
                  <p className="text-xs text-gray-500 mb-0.5">{item.label}</p>
                  <p className="text-sm font-semibold text-gray-800">{item.value}</p>
                </div>
              ))}
            </div>
            <div className="flex justify-end pt-4">
              <Button variant="danger" icon={Trash2} onClick={() => setModalType('delete')}>Delete Admin</Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Delete Modal */}
      <Modal open={modalType === 'delete'} onClose={() => setModalType(null)} title="Confirm Delete" size="sm">
        {selected && (
          <div className="text-center space-y-4">
            <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto">
              <Trash2 size={24} className="text-red-600" />
            </div>
            <p className="text-gray-700">Are you sure you want to delete <strong>{selected.name}</strong>? This action cannot be undone.</p>
            <div className="flex gap-3">
              <Button variant="secondary" onClick={() => setModalType(null)} className="flex-1">Cancel</Button>
              <Button variant="danger" onClick={handleDelete} className="flex-1" icon={Trash2}>Delete</Button>
            </div>
          </div>
        )}
      </Modal>
    </Layout>
  );
};

export default ManageAdmins;
